import { FC } from 'react';
import { createPortal } from 'react-dom';
import {
  Backdrop,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from '@mui/material';
import clsx from 'clsx';
import useDisbursments from './hooks/disbursments';
import { useDisbursmentsVariant } from './hooks/use-disbursments-variant';
import DisbursmentRow from './components/disbursmentRow';
import { CardGrid } from './components/disbursmentCard/cardGrid';
import { DisbursmentCard } from './components/disbursmentCard/disbursmentCard';
import SkeletonDisbursments from './components/skeletonDisbursment';
import { useTranslation } from 'react-i18next';
import { DeleteConfirmationModal } from 'components/delete-confirmation';
import ModalAddEntry from './components/modalAddEntry';
import { filteredAccountingLinks } from './services';
import ModalEditEntry from './components/modalEditEntry';
import { AddAndUpdatePartyLedgerEntry } from 'components/add-and-update-party-ledger-modal/add-and-update-party-ledger-entry';
import { Balancing } from './components/balancing/balancing';
import { LedgerBalance } from "./components/ledger-balance";
import { useStyles } from './styles';

type kindEntryProp = 'disbursement' | 'receipt' | 'netfunded' | 'proceeds';

type DisbursmentsProps = {
  kindEntry: kindEntryProp[];
  titleTab: 'ledger' | 'disbursements' | 'receipts' | 'netfunded' | 'proceeds';
};

const Disbursments: FC<DisbursmentsProps> = ({ kindEntry, titleTab }) => {
  const classes = useStyles();
  const variant = useDisbursmentsVariant();
  const isCard = variant === 'card';
  const {
    accounting,
    disbursments,
    isLoading,
    modals,
    handleModals,
    handleDeleteEntry,
    isLoadingMutations,
    confirmDelate,
    handleConfirmDelete,
    handleCreationEntry,
    handleEditEntry,
    handleUpdatePosted,
    itemsCollapsed,
    setItemsCollapsed,
    openDeleteAssociationModal
  } = useDisbursments(kindEntry);

  const { openConfirm, callbackSuccess, title, confirmationMessage } = confirmDelate;
  const { t } = useTranslation();
  const tableHeadCell = [
    { id: 'actions', label: "Actions", hide: kindEntry.length > 1 || titleTab === "proceeds", textAlign: 'start' },
    { id: 'description', label: t('disbursments:description'), hide: false, textAlign: 'start' },
    { id: 'posted', label: t('disbursments:posted'), hide: kindEntry.length > 1 || titleTab === "proceeds", textAlign: 'start' },
    { id: 'amount', label: t('disbursments:amount'), hide: kindEntry.length > 1, textAlign: 'start' },
    { id: 'method', label: t('disbursments:method'), hide: titleTab === "proceeds", textAlign: 'start' },
    { id: 'date', label: t('disbursments:date'), hide: titleTab === "proceeds", textAlign: 'start' },
    { id: 'accounted-for', label: t('disbursments:accounted-for'), hide: kindEntry.length > 1 || titleTab === "proceeds", textAlign: 'start' },
    { id: 'options', label: t('disbursments:options'), hide: kindEntry.length > 1 || titleTab === "proceeds", textAlign: 'start' },
    { id: 'debit', label: t('disbursments:debit'), hide: kindEntry.length <= 1 || titleTab === "proceeds", textAlign: 'center' },
    { id: 'credit', label: t('disbursments:credit'), hide: kindEntry.length <= 1 || titleTab === "proceeds", textAlign: 'center' },
  ];

  // Same data and callbacks feed both presentations (table rows and cards).
  const entryProps = {
    handleDeleteEntry,
    handleConfirmDelete,
    t,
    kindEntry,
    handleModals,
    handleUpdatePosted,
    itemsCollapsed,
    setItemsCollapsed,
    openDeleteAssociationModal,
  };

  const emptyEntries = (
    <Box className={classes.containerEmpty}>
      <Typography variant="h6">{t('disbursments:empty-entry')}</Typography>
    </Box>
  );

  const cardsContent = disbursments.length > 0 ? (
    <CardGrid>
      {disbursments.map((item) => (
        <DisbursmentCard disbursmentItem={item} key={item._id} {...entryProps} />
      ))}
    </CardGrid>
  ) : emptyEntries;

  const tableContent = (
    <TableContainer style={{ width: titleTab === "proceeds" ? "50%" : "100%" }}>
      {disbursments.length > 0 ? (
        <Table>
          <TableHead>
            <TableRow style={{ height: "68px" }}>
              {tableHeadCell
                .filter((cell) => !cell.hide)
                .map((cell) => (
                  <TableCell className={classes.allcells} key={cell.id}>{cell.label}</TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {disbursments.map((item) => (
              <DisbursmentRow disbursmentItem={item} key={item._id} {...entryProps} />
            ))}
          </TableBody>
        </Table>
      ) : emptyEntries}
    </TableContainer>
  );

  return (
    <div className={clsx(classes.container, { [classes.containerStacked]: isCard })}>
      {titleTab === "ledger" && document.getElementById('ledger-balance-totals') && createPortal((
        <LedgerBalance disbursements={disbursments} />
      ), document.getElementById('ledger-balance-totals')!)}
      <Paper className={clsx(classes.disbursementsContainer, { [classes.disbursementsContainerStacked]: isCard })}>
        {isLoading && disbursments.length === 0 ? (
          <SkeletonDisbursments variant={variant} />
        ) : (
          <>
            {!['ledger', 'proceeds'].includes(titleTab) && createPortal(
              <Button
                variant="contained"
                color="secondary"
                className={classes.addButton}
                onClick={() => handleModals('add')}
                style={{ textTransform: 'capitalize' }}
              >
                {t('disbursments:add-entry')}
              </Button>, document.getElementById('disbursements-add-entry-portal')!
            )}
            {isCard ? cardsContent : tableContent}
            {modals && (
              <>
                {modals.kind === 'add' && (
                  <ModalAddEntry
                    accounting={filteredAccountingLinks(accounting, kindEntry)}
                    handleClose={() => {
                      handleModals('');
                    }}
                    t={t}
                    kindEntry={kindEntry}
                    handleCreationEntry={handleCreationEntry}
                  />
                )}
                {modals.kind === 'edit' && (
                  <ModalEditEntry
                    accounting={disbursments.filter((entry) => entry._id === modals.idEntry)[0]}
                    handleClose={() => {
                      handleModals('');
                    }}
                    t={t}
                    handleEditEntry={handleEditEntry}
                    kindEntry={kindEntry[0]}
                  />
                )}
                {modals.kind === "addAssociation" && (
                  <AddAndUpdatePartyLedgerEntry
                    handleClose={() => handleModals("")}
                    entryLedger={modals.entry!}
                    partyLedgerEntry={modals.partyEntry}
                    kindAllowedAssociationList={["Payee", "Payer", "On behalf of"]}
                    hideFields={{
                      amount: !modals.entry!.posted,
                      percent: !modals.entry!.posted,
                      at_closing_amount: true,
                      at_closing_percent: true,
                      before_closing_amount: true,
                      before_closing_percent: true,
                    }}
                  />
                )}
              </>
            )}
          </>
        )}
      </Paper>

      {!isCard && (
        <Paper className={classes.balancingContainer}>
          <Balancing />
        </Paper>
      )}

      <Backdrop className={classes.backdrop} open={isLoadingMutations}>
        <CircularProgress color="secondary" />
      </Backdrop>

      {callbackSuccess && (
        <DeleteConfirmationModal
          title={title}
          confirmationMessage={confirmationMessage}
          isOpen={openConfirm}
          onConfirm={callbackSuccess}
          onClose={() => handleConfirmDelete(false, null, '', '')}
          isRemoveLoading={false}
          buttonConfirmText={t('disbursments:agree')}
        />
      )}
    </div>
  );
};

export default Disbursments;
